import { memo } from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalHeader, useDisclosure } from '@heroui/react';
import {
  getNotificationCreatedByLabel,
  useDeleteNotificationMutation,
  type INotification,
} from '@/entities/Notifications';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import { convertIsoToDate, convertIsoToTime } from '@/shared/lib/utils/dateAdapters';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { MessageModal } from '@/shared/ui/Modal';
import { Svg } from '@/shared/ui/Svg';
import styles from './PushNotificationDetailModal.module.scss';

interface PushNotificationDetailModalProps {
  notification: INotification | null;
  onClose: () => void;
}

export const PushNotificationDetailModal = memo(
  ({ notification, onClose }: PushNotificationDetailModalProps) => {
    const {
      isOpen: isConfirmOpen,
      onOpen: onConfirmOpen,
      onOpenChange: onConfirmOpenChange,
    } = useDisclosure();
    const [deleteNotification, { isLoading: isDeleting }] = useDeleteNotificationMutation();

    const handleDelete = async () => {
      if (!notification) return;

      try {
        await deleteNotification(notification.id).unwrap();
        onConfirmOpenChange();
        onClose();
      } catch {
        // keep modal open on error
      }
    };

    return (
      <>
        <Modal
          isOpen={!!notification}
          onOpenChange={(isOpen) => {
            if (!isOpen) onClose();
          }}
          placement="center"
          size="2xl"
          backdrop="opaque"
          scrollBehavior="inside"
          motionProps={scaleInOut}
          classNames={{
            base: styles.modalBase,
            closeButton: styles.closeButton,
          }}
        >
          <ModalContent>
            {notification ? (
              <>
                <ModalHeader className={styles.titleRow}>
                  <span className={styles.title}>{notification.title}</span>
                  <Button
                    isIconOnly
                    variant="light"
                    className={styles.deleteButton}
                    aria-label="Delete notification"
                    onPress={onConfirmOpen}
                  >
                    <Svg Icon={TrashIcon} width={20} height={20} className="text-red-tertiary" />
                  </Button>
                </ModalHeader>
                <ModalBody className={styles.body}>
                  <div className={styles.detailsCard}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Time Sent</span>
                      <span className={styles.detailValue}>
                        {convertIsoToDate(notification.createdAt)} ·{' '}
                        {convertIsoToTime(notification.createdAt)}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Created by</span>
                      <span className={styles.detailValue}>
                        {getNotificationCreatedByLabel(notification.createdByUser)}
                      </span>
                    </div>
                    {notification.link ? (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Link</span>
                        <span className={styles.detailValue}>{notification.link}</span>
                      </div>
                    ) : null}
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Message</span>
                      <p className={styles.messageText}>{notification.message}</p>
                    </div>
                  </div>
                </ModalBody>
              </>
            ) : null}
          </ModalContent>
        </Modal>

        <MessageModal
          isOpen={isConfirmOpen}
          onOpenChange={() => onConfirmOpenChange()}
          title="Delete notification"
          text="Are you sure you want to delete this notification?"
          confirmText="Delete"
          variant="red"
          isLoading={isDeleting}
          toggleVisibility={handleDelete}
        />
      </>
    );
  },
);
